import React, { useState, useEffect, useCallback } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";
import { colors } from "../AgenticPaymentAirdrop/styles";
import { withCountry } from "../../utils/currencyCountry";
import { HelpTitle } from "../AdminHeaderHelp";
import {
  card, h2, note, help, th, thNum, td, tdNum, mono, tableBox, btn, ghostBtn, dangerBtn, linkBtn,
  Tag, Banner, num, amount, when, sentence, percent, signed, signedTone, ReasonField, readyToOpen, readyToSplit,
  isLegacyPool, sortRows, useSort,
} from "../PoolProtection/shared";

// The one-time pool setup tools, kept off Pool Protection so that page holds
// only the daily work: putting pools at exactly 100% (the profit ledger),
// splitting each pool's opening into rate moves and corrections, and revaluing
// every pool on the ledger now. Same lp-api calls as before, super admin only.

// Every pool on the ledger has a saved split. Returns the latest save date, or
// null while a pool is still missing one.
export const splitDoneAt = (pools) => {
  const onLedger = (pools || []).filter((p) => p.onLedger);
  if (onLedger.length === 0 || !onLedger.every((p) => p.openingSplit)) return null;
  return onLedger.map((p) => p.openingSplit.replayedAt).filter(Boolean).sort().pop() || "";
};

// What each Opening adjustment header sorts by (see sortRows).
const ADJUSTMENT_SORTS = {
  currency: { text: true, value: (p) => p.currencySymbol },
  adjustment: { value: (p) => p.adjustmentUsdb },
  fx: { value: (p) => p.openingSplit?.fx },
  correction: { value: (p) => p.openingSplit?.correction },
  since: { value: (p) => (p.ledgerSince ? Date.parse(p.ledgerSince) : null) },
};

// What each Check pools result header sorts by.
// What the "?" after each Check pools header says.
const CHECK_HELP = {
  before: "USDb this pool holds before the run.",
  after: "What customers are owed at today's rate. The pool holds exactly this after the run.",
  backing: "In pool now ÷ at 100%.",
  adjustment: "In pool now minus at 100%. Plus means the pool held more than customers were owed, minus means it held less. It is recorded as an opening adjustment, not profit.",
};
// What the "?" after the opening headers says, in Opening breakdown and Opening adjustment.
const OPENING_HELP = {
  opening: "What the pool held minus what customers were owed when it went to 100%. Plus is a surplus, minus a shortfall. Not counted as profit.",
  fx: "What customers' swapped tokens gained or lost between each swap day and the opening day, valued at each day's mid rate.",
  correction: "The rest of the opening: spread the old model left in the pool, swaps booked at the wrong rate, fees burned without a record, and tokens that reached customers outside a swap. Rate moves plus corrections always equals the opening.",
  swaps: "Customer swaps on this token before the opening that were replayed. Swaps before the rate history had no daily rate, and internal wallet swaps are left out.",
};
const CHECK_SORTS = {
  currency: { text: true, value: (r) => r.currencySymbol },
  before: { value: (r) => r.before },
  after: { value: (r) => r.after },
  backing: { value: (r) => r.backingBefore },
  adjustment: { value: (r) => r.openingUsdb },
};

// Pools whose opening and corrections came to something when they were put at
// 100%. A signing server older than adjustmentUsdb has none, so none show.
export const adjustedPools = (pools) =>
  (pools || []).filter((p) => !isLegacyPool(p) && num(p.adjustmentUsdb) !== 0);

const PoolSetup = ({ overviewUrl, openLedgerUrl, replayUrl, revalueUrl, protectionUrl }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [message, setMessage] = useState(null); // { tone, text }
  const [report, setReport] = useState(null); // the last ledger opening report, dry run or real
  const [runReason, setRunReason] = useState("");
  const [split, setSplit] = useState(null); // the last opening split report, dry run or saved
  const [splitReason, setSplitReason] = useState("");
  const [rerun, setRerun] = useState(false); // shows the split button again once it is done
  const [busy, setBusy] = useState(false);
  const { sort, sortTh } = useSort(ADJUSTMENT_SORTS, { key: "currency", dir: "asc" });
  const check = useSort(CHECK_SORTS, { key: "currency", dir: "asc" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest(overviewUrl);
      if (res?.success) {
        setData(res.data);
        setLoadError(null);
      } else {
        setLoadError(res?.error || "Couldn't load the pools.");
      }
    } catch (e) {
      setLoadError("Couldn't load the pools.");
    } finally {
      setLoading(false);
    }
  }, [overviewUrl]);

  useEffect(() => { load(); }, [load]);

  // A dry run when `dryRun`, otherwise the real run for the pools the last dry
  // run said were ready, so it does exactly what was checked.
  const openLedger = async (dryRun) => {
    const body = { dryRun };
    if (!dryRun) {
      const reason = runReason.trim();
      if (!reason) return setMessage({ tone: "bad", text: "Enter a reason." });
      Object.assign(body, { reason, tokenIds: readyToOpen(report).map((r) => r.tokenId) });
    }
    setBusy(true);
    setMessage(null);
    try {
      const res = await postRequest(openLedgerUrl, body);
      if (!res?.success) return setMessage({ tone: "bad", text: res?.error || "That didn't run." });
      setReport(res.data);
      if (!dryRun) {
        const opened = (res.data?.pools || []).filter((r) => r.opened).length;
        setMessage({ tone: "good", text: `${opened} ${opened === 1 ? "pool is" : "pools are"} at 100% now.` });
        setRunReason("");
        await load();
      }
    } catch (e) {
      setMessage({ tone: "bad", text: e?.data?.error || "That didn't run." });
    } finally {
      setBusy(false);
    }
  };

  // Same as openLedger: a dry run, or saving exactly the pools it checked.
  const replay = async (dryRun) => {
    const body = { dryRun };
    if (!dryRun) {
      const reason = splitReason.trim();
      if (!reason) return setMessage({ tone: "bad", text: "Enter a reason." });
      Object.assign(body, { reason, tokenIds: readyToSplit(split).map((r) => r.tokenId) });
    }
    setBusy(true);
    setMessage(null);
    try {
      const res = await postRequest(replayUrl, body);
      if (!res?.success) return setMessage({ tone: "bad", text: res?.error || "That didn't run." });
      setSplit(res.data);
      if (!dryRun) {
        const saved = readyToSplit(res.data).length;
        setMessage({ tone: "good", text: `Breakdown saved for ${saved} ${saved === 1 ? "pool" : "pools"}.` });
        setSplitReason("");
        await load();
      }
    } catch (e) {
      setMessage({ tone: "bad", text: e?.data?.error || "That didn't run." });
    } finally {
      setBusy(false);
    }
  };

  const revalue = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await postRequest(revalueUrl, {});
      if (!res?.success) return setMessage({ tone: "bad", text: res?.error || "That didn't run." });
      const done = res.data?.revalued || [];
      setMessage({ tone: "good", text: done.length ? `Revalued ${done.map(withCountry).join(", ")}.` : "Nothing to revalue." });
      await load();
    } catch (e) {
      setMessage({ tone: "bad", text: e?.data?.error || "That didn't run." });
    } finally {
      setBusy(false);
    }
  };

  const tradingPaused = !!data?.settings?.tradingPaused;
  const ready = readyToOpen(report);
  const splitReady = readyToSplit(split);
  const doneAt = splitDoneAt(data?.pools);
  const showSplitButton = doneAt == null || rerun;
  const adjusted = adjustedPools(data?.pools);
  const adjustedTotal = adjusted.reduce((s, p) => s + num(p.adjustmentUsdb), 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap", marginBottom: "20px" }}>
        <p style={{ ...note, margin: 0 }}>Setup tools for the pools. All runs are recorded in the audit log.</p>
        <button type="button" style={ghostBtn(loading)} disabled={loading} onClick={load}>{loading ? "Refreshing…" : "Refresh"}</button>
      </div>
      {loadError && <Banner tone="bad">{loadError} Refresh to try again.</Banner>}
      {tradingPaused && (
        <Banner tone="bad">
          <strong>All trading is paused.</strong>
          {data.settings.tradingPausedReason && <> Why: {data.settings.tradingPausedReason}.</>}
          {data.settings.tradingPausedAt && <> Since {when(data.settings.tradingPausedAt)}.</>}
        </Banner>
      )}
      {message && <Banner tone={message.tone}>{message.text}</Banner>}

      <div style={card}>
        <h2 style={h2}>Balance pools to 100% {tradingPaused ? <Tag tone="bad">Trading paused</Tag> : <Tag tone="good">Trading on</Tag>}</h2>
        <p style={note}>
          Sets each pool to exactly what its customers are owed at today's rate. Any difference is recorded as an opening adjustment, not profit.
          Customer balances don't change.
        </p>
        <ol style={{ ...note, paddingLeft: "20px" }}>
          <li>Pause all trading on <a href={protectionUrl}>Pool Protection</a>.</li>
          <li>Check the pools, then run it.</li>
          <li>Check that each pool reads 100%.</li>
          <li>Resume all trading on <a href={protectionUrl}>Pool Protection</a>.</li>
        </ol>
        <button type="button" style={btn(busy)} disabled={busy} onClick={() => openLedger(true)}>Check pools</button>
          {report && (
            <div style={{ marginTop: "16px" }}>
              <p style={{ ...note, fontWeight: 600, color: colors.gray700 }}>
                {report.dryRun ? "Check only. Nothing has changed yet." : "Run finished."}
              </p>
              {(report.pools || []).length === 0 ? <p style={note}>No pool to balance to 100%.</p> : (
                <div style={tableBox()}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {check.sortTh("currency", "Currency")}{check.sortTh("before", "In pool now (USDb)", true, CHECK_HELP.before)}
                        {check.sortTh("after", "At 100% (USDb)", true, CHECK_HELP.after)}
                        {check.sortTh("backing", "Backing now", true, CHECK_HELP.backing)}
                        {check.sortTh("adjustment", "Adjustment (USDb)", true, CHECK_HELP.adjustment)}<th style={th}>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortRows(report.pools, check.sort, CHECK_SORTS).map((r) => (
                        <tr key={r.tokenId}>
                          <td style={td}><strong>{withCountry(r.currencySymbol)}</strong><div style={{ ...mono, color: colors.gray600 }}>{r.tokenId}</div></td>
                          <td style={tdNum}>{r.before == null ? "-" : amount(r.before)}</td>
                          <td style={tdNum}>{r.after == null ? "-" : amount(r.after)}</td>
                          <td style={tdNum}>{r.backingBefore == null ? "-" : percent(r.backingBefore)}</td>
                          <td style={{ ...tdNum, ...signedTone(r.openingUsdb) }}>{r.openingUsdb == null ? "-" : signed(r.openingUsdb)}</td>
                          <td style={td}>
                            {r.opened ? <Tag tone="good">Done</Tag>
                              : r.skipped ? <><Tag tone="info">Skipped</Tag><div style={help}>{sentence(r.skipped)}.</div></>
                              : <Tag tone="warn">Ready</Tag>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {report.dryRun && ready.length > 0 && (
                <div style={{ marginTop: "16px", padding: "16px", border: `1px solid ${colors.border}`, borderRadius: "8px", background: colors.gray100 }}>
                  <p style={{ margin: "0 0 8px", fontSize: "14px" }}>
                    Balance {ready.length} {ready.length === 1 ? "pool" : "pools"} to 100%? Adjustment: {signed(ready.reduce((s, r) => s + num(r.openingUsdb), 0))} USDb.
                  </p>
                  <div style={{ marginBottom: "12px" }}>
                    <ReasonField id="run-reason" value={runReason} onChange={setRunReason} />
                  </div>
                  <button type="button" style={dangerBtn(busy || !tradingPaused)} disabled={busy || !tradingPaused} onClick={() => openLedger(false)}>
                    Run it
                  </button>
                  <button type="button" style={{ ...ghostBtn(busy), marginLeft: "8px" }} disabled={busy} onClick={() => setReport(null)}>Cancel</button>
                </div>
              )}
            </div>
          )}
      </div>

      <div style={card}>
        <h2 style={h2}>Opening breakdown {doneAt != null && <Tag tone="good">Done</Tag>}</h2>
        <p style={note}>How much of each pool's opening balance came from rate changes and how much from corrections.</p>
        {doneAt != null && (
          <p style={note}>
            {doneAt ? `Saved on ${when(doneAt)}. ` : "Saved. "}
            {!rerun && <button type="button" style={linkBtn} onClick={() => setRerun(true)}>Run it again</button>}
          </p>
        )}
        {showSplitButton && (
          <button type="button" style={doneAt == null ? btn(busy) : ghostBtn(busy)} disabled={busy} onClick={() => replay(true)}>Opening breakdown</button>
        )}
          {split && (
            <div style={{ marginTop: "16px" }}>
              <p style={{ ...note, fontWeight: 600, color: colors.gray700 }}>
                {split.dryRun ? "Opening breakdown, check only. Nothing is saved yet." : "Opening breakdown saved."}
              </p>
              {!split.dryRun && split.sha256 && (
                <p style={note}>
                  Audit hash <span style={mono}>{split.sha256}</span>
                  {split.hcsTransactionId && <> on Hedera in <span style={mono}>{split.hcsTransactionId}</span></>}
                </p>
              )}
              {(split.pools || []).length === 0 ? <p style={note}>No pool has an opening to break down.</p> : (
                <div style={tableBox()}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={th}>Currency</th><th style={thNum}><HelpTitle title="Opening (USDb)" help={OPENING_HELP.opening} /></th>
                        <th style={thNum}><HelpTitle title="Rate moves (USDb)" help={OPENING_HELP.fx} /></th>
                        <th style={thNum}><HelpTitle title="Corrections (USDb)" help={OPENING_HELP.correction} /></th>
                        <th style={thNum}><HelpTitle title="Swaps" help={OPENING_HELP.swaps} /></th><th style={th}>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {split.pools.map((r) => (
                        <tr key={r.tokenId}>
                          <td style={td}><strong>{withCountry(r.currencySymbol)}</strong><div style={{ ...mono, color: colors.gray600 }}>{r.tokenId}</div></td>
                          <td style={{ ...tdNum, ...signedTone(r.openingUsdb) }}>{r.openingUsdb == null ? "-" : signed(r.openingUsdb)}</td>
                          <td style={{ ...tdNum, ...signedTone(r.fxUsdb) }}>{r.fxUsdb == null ? "-" : signed(r.fxUsdb)}</td>
                          <td style={{ ...tdNum, ...signedTone(r.correctionUsdb) }}>{r.correctionUsdb == null ? "-" : signed(r.correctionUsdb)}</td>
                          <td style={tdNum}>
                            {r.legs == null ? "-" : num(r.legs).toLocaleString("en-US")}
                            {num(r.legsBeforeHistory) > 0 && <div style={help}>{num(r.legsBeforeHistory).toLocaleString("en-US")} before the rate history</div>}
                            {num(r.legsSkipped) > 0 && <div style={help}>{num(r.legsSkipped).toLocaleString("en-US")} internal, left out</div>}
                          </td>
                          <td style={td}>
                            {r.skipped ? <><Tag tone="info">Skipped</Tag><div style={help}>{sentence(r.skipped)}.</div></>
                              : split.dryRun ? <Tag tone="warn">Ready</Tag> : <Tag tone="good">Saved</Tag>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {split.dryRun && splitReady.length > 0 && (
                <div style={{ marginTop: "16px", padding: "16px", border: `1px solid ${colors.border}`, borderRadius: "8px", background: colors.gray100 }}>
                  <p style={{ margin: "0 0 8px", fontSize: "14px" }}>
                    Save this breakdown for {splitReady.length} {splitReady.length === 1 ? "pool" : "pools"}?
                  </p>
                  <div style={{ marginBottom: "12px" }}>
                    <ReasonField id="split-reason" value={splitReason} onChange={setSplitReason} />
                  </div>
                  <button type="button" style={btn(busy)} disabled={busy} onClick={() => replay(false)}>Save</button>
                  <button type="button" style={{ ...ghostBtn(busy), marginLeft: "8px" }} disabled={busy} onClick={() => setSplit(null)}>Cancel</button>
                </div>
              )}
            </div>
          )}
      </div>

      <div style={card}>
        <h2 style={h2}>Opening adjustment</h2>
        <p style={note}>Difference recorded when each pool was balanced to 100%. Not counted as profit.</p>
        {!data ? (loading && <p style={{ ...note, margin: 0 }}>Loading…</p>)
          : adjusted.length === 0 ? <p style={{ ...note, margin: 0 }}>No pool has an opening adjustment.</p> : (
          <>
            <p style={{ margin: "0 0 12px", fontSize: "14px" }}>
              Total: <strong style={signedTone(adjustedTotal)}>{signed(adjustedTotal)} USDb</strong> across {adjusted.length} {adjusted.length === 1 ? "pool" : "pools"}
            </p>
            <div style={tableBox()}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {sortTh("currency", "Currency")}{sortTh("adjustment", "Opening adjustment (USDb)", true, OPENING_HELP.opening)}
                    {sortTh("fx", "Rate moves (USDb)", true, OPENING_HELP.fx)}{sortTh("correction", "Corrections (USDb)", true, OPENING_HELP.correction)}
                    {sortTh("since", "At 100% since")}
                  </tr>
                </thead>
                <tbody>
                  {sortRows(adjusted, sort, ADJUSTMENT_SORTS).map((p) => (
                    <tr key={p.tokenId}>
                      <td style={td}><strong>{withCountry(p.currencySymbol)}</strong><div style={{ ...mono, color: colors.gray600 }}>{p.tokenId}</div></td>
                      <td style={{ ...tdNum, ...signedTone(p.adjustmentUsdb) }}>{signed(p.adjustmentUsdb)}</td>
                      <td style={{ ...tdNum, ...signedTone(p.openingSplit?.fx) }}>{p.openingSplit?.fx == null ? "-" : signed(p.openingSplit.fx)}</td>
                      <td style={{ ...tdNum, ...signedTone(p.openingSplit?.correction) }}>{p.openingSplit?.correction == null ? "-" : signed(p.openingSplit.correction)}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{p.ledgerSince ? when(p.ledgerSince) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div style={card}>
        <h2 style={h2}>Update to today's rate</h2>
        <p style={note}>
          Revalue every pool at today's rate. Runs automatically every {data?.revalueEveryMinutes ?? 5} minutes.
        </p>
        <button type="button" style={ghostBtn(busy)} disabled={busy} onClick={revalue}>Revalue now</button>
      </div>
    </div>
  );
};

export default PoolSetup;
